import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { $$, delay } from "./recruit-utils.js";

/**
 * Recruit Animation - Data Counter
 * データカード数字カウントアップアニメーション
 */

export function initDataCountUpAnimation() {
  // 従来のHTML要素での処理
  const dataNumbers = $$('.p-recruit-data-card__number');
  
  if (dataNumbers.length) {
    gsap.set(dataNumbers, { opacity: 0 });

    dataNumbers.forEach((numberElement) => {
      const valueElements = numberElement.querySelectorAll('.p-recruit-data-card__value');
      
      if (!valueElements.length) return;

      let hasTriggered = false;

      ScrollTrigger.create({
        trigger: numberElement,
        start: 'top 80%',
        end: 'bottom 20%',
        fastScrollEnd: true,
        refreshPriority: -1, // 他のScrollTriggerより後にrefresh
        onEnter: () => {
          if (!hasTriggered) {
            hasTriggered = true;
            animateNumber(numberElement, valueElements);
          }
        }
      });
    });
  }

  // SVG内の数字に対する処理
  const dataSvgs = $$('.p-recruit-data-card svg');
  
  if (dataSvgs.length) {
    dataSvgs.forEach((svg) => {
      // 属性とstyle属性の両方に対応
      let numberSpans = Array.from(svg.querySelectorAll('text[font-family="Inter"] tspan'));
      
      // style属性内のfont-familyもチェック
      if (!numberSpans.length) {
        const allTextElements = Array.from(svg.querySelectorAll('text tspan'));
        numberSpans = allTextElements.filter(span => {
          const parent = span.parentElement;
          const style = parent.getAttribute('style') || '';
          const fontFamily = parent.getAttribute('font-family') || '';
          return style.includes('font-family="Inter"') || style.includes("font-family='Inter'") || fontFamily.includes('Inter');
        });
      }
      
      if (!numberSpans.length) return;

      // x座標で並び替え（左から右へ）
      numberSpans.sort((a, b) => {
        const xA = parseFloat(a.getAttribute('x') || '0');
        const xB = parseFloat(b.getAttribute('x') || '0');
        return xA - xB;
      });

      // 初期状態でopacity 0に設定（すべてのspanに確実に適用）
      gsap.set(numberSpans, { opacity: 0, visibility: 'visible' });

      let hasTriggered = false;

      ScrollTrigger.create({
        trigger: svg,
        start: 'top 80%',
        end: 'bottom 20%',
        fastScrollEnd: true,
        refreshPriority: -1, // 他のScrollTriggerより後にrefresh
        onEnter: () => {
          if (!hasTriggered) {
            hasTriggered = true;
            animateSvgNumber(svg, numberSpans);
          }
        }
      });
    });
  }
}

async function animateNumber(numberElement, valueElements) {
  // 数字要素を表示
  gsap.to(numberElement, { 
    opacity: 1, 
    duration: 0.3,
    ease: "power2.out"
  });

  // 各数字を順次アニメーション
  for (let i = 0; i < valueElements.length; i++) {
    const valueEl = valueElements[i];
    const targetValue = parseInt(valueEl.textContent);
    
    if (isNaN(targetValue)) continue;

    // カウントアップアニメーション
    const counter = { value: 0 };
    
    gsap.to(counter, {
      value: targetValue,
      duration: 1.5,
      delay: i * 0.2,
      ease: "power2.out",
      onUpdate: () => {
        valueEl.textContent = Math.floor(counter.value);
      },
      onComplete: () => {
        valueEl.textContent = targetValue; // 最終値を確実にセット
      }
    });

    // 少し遅延を入れて次の数字へ
    if (i < valueElements.length - 1) {
      await delay(100);
    }
  }
}

async function animateSvgNumber(svgElement, numberSpans) {
  // 数字要素を表示（すべてのspanを確実に表示）
  gsap.to(numberSpans, { 
    opacity: 1, 
    visibility: 'visible',
    duration: 0.3,
    ease: "power2.out"
  });

  // 完全な数値を取得（複数のtspanから結合）
  const fullNumberText = numberSpans.map(span => span.textContent).join('');
  // カンマを除去してから数値化
  const targetValue = parseInt(fullNumberText.replace(/,/g, ''));
  
  if (isNaN(targetValue)) return;

  // 1桁の場合は単一のspan処理、複数の場合は各spanを個別にアニメーション
  if (numberSpans.length === 1) {
    // 1桁の場合は従来のHTML要素と同じ処理
    const span = numberSpans[0];
    const counter = { value: 0 };
    
    gsap.to(counter, {
      value: targetValue,
      duration: 1.5,
      ease: "power2.out",
      onUpdate: () => {
        span.textContent = Math.floor(counter.value).toLocaleString();
      },
      onComplete: () => {
        span.textContent = targetValue.toLocaleString();
      }
    });
  } else {
    // 複数spanの場合：各spanの初期値を保持し、その比率でアニメーション
    const initialValues = numberSpans.map(span => parseInt(span.textContent.replace(/,/g, '') || '0'));
    const counter = { value: 0 };
    
    gsap.to(counter, {
      value: targetValue,
      duration: 1.5,
      ease: "power2.out",
      onUpdate: () => {
        const currentValue = Math.floor(counter.value);
        const ratio = targetValue > 0 ? currentValue / targetValue : 0;
        
        // 各spanを初期値の比率でアニメーション
        numberSpans.forEach((span, index) => {
          const spanTargetValue = initialValues[index];
          const spanCurrentValue = Math.floor(spanTargetValue * ratio);
          span.textContent = spanCurrentValue.toLocaleString();
          span.style.opacity = '1';
          span.style.visibility = 'visible';
        });
      },
      onComplete: () => {
        // 最終値を確実にセット（元の値に戻す）
        numberSpans.forEach((span, index) => {
          span.textContent = initialValues[index].toLocaleString();
          span.style.opacity = '1';
          span.style.visibility = 'visible';
        });
      }
    });
  }
}